import * as React from "react";
import { uniqueId, flatMap } from "lodash";
import { AccuracyTemplate, AccuracyTemplateInspectionGroup } from "@app/templates/accuracy/models";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { AccuracyTemplateContext } from "@app/templates/accuracy/components";
import CreateReportState, { RouteStateInspectorGroups } from "@app/projects/network-assessment/accuracy/createReportState";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";

interface AccuracyInspectorSelectFlyoutProps {
  exportEndpoint: string;
  createReportLinkState: CreateReportState;
  projectConfiguration: ProjectRunConfiguration;
}

interface AccuracyInspectorSelectFlyoutState {
  selectedInspectors: string[];
}

type AccuracyInspectorSelectFlyoutCombinedProps = AccuracyInspectorSelectFlyoutProps & RouteComponentProps;

class AccuracyInspectorSelectFlyout extends React.Component<AccuracyInspectorSelectFlyoutCombinedProps, AccuracyInspectorSelectFlyoutState> {
  private inspectionGroups: AccuracyTemplateInspectionGroup[] = [];

  constructor(props: AccuracyInspectorSelectFlyoutCombinedProps) {
    super(props);

    this.state = {
      selectedInspectors: []
    };
  }

  public render() {
    const createReportLinkState = { ...this.props.createReportLinkState };
    createReportLinkState.inspectionUids = this.state.selectedInspectors;
    const selectedInspectors: RouteStateInspectorGroups[] = [];
    this.inspectionGroups.forEach((ig) => {
      const group: RouteStateInspectorGroups = {
        inspectors: [],
        name: ig.name,
      };
      ig.inspections.forEach((i) => {
        if (this.state.selectedInspectors.includes(i.uid)) {
          group.inspectors.push(i.name);
        }
      })

      selectedInspectors.push(group);
    })
    createReportLinkState.selectedInspectors = selectedInspectors;
    createReportLinkState.allInspectorsSelected = this.allInspectorsSelected();

    const handleConfirm = () => {
      this.props.history.push(
        {
          pathname: this.props.exportEndpoint,
          state: createReportLinkState
        }
      );
    }

    return (
      <>
        <AccuracyTemplateContext.Consumer>
          {(accuracyTemplate) => (
            this.renderInspectorSelect(accuracyTemplate)
          )}
        </AccuracyTemplateContext.Consumer>
        <FlyoutFooter
          submitButton={
            <QESButton qesTestId="inspector-select-confirm" onClick={handleConfirm} disabled={this.state.selectedInspectors.length <= 0}>
              Confirm
            </QESButton>
          }
        />
      </>
    );
  }

  private storeInspectionGroups = (accuracyTemplate: AccuracyTemplate) => {
    const inspectionGroups: AccuracyTemplateInspectionGroup[] = [];

    for (const inspectionGroup of accuracyTemplate.criteria.inspectionGroups) {
      if (!inspectionGroups.includes(inspectionGroup)) {
        inspectionGroups.push(inspectionGroup);
      }
    }

    this.inspectionGroups = inspectionGroups;
  }

  private renderInspectorSelect(accuracyTemplate: AccuracyTemplate) {
    this.storeInspectionGroups(accuracyTemplate);

    const {
      excludeAccuracyPractitionerAtLocationInspector: excludePracAtLoc,
      excludeProviderClaimsActivityInspector: excludePca
    } = this.props.projectConfiguration;

    const exclusion = (inspection: { name: string; }) => {
      if (excludePracAtLoc && inspection.name === "Practitioner At Location Confidence") {
        return false;
      }
      if (excludePca && inspection.name === "Provider Claims Activity") {
        return false;
      }
      return true;
    };

    const inspectionGroups = accuracyTemplate.criteria.inspectionGroups.map(g => ({
      ...g,
      inspections: g.inspections.filter(exclusion)
    }));
    
    const inspectionGroupRender = inspectionGroups.map((g) => (
      <div className="ms-4 mb-4" key={g.uid}>
        <QESCheckbox
          id={uniqueId(g.name)}
          key={g.name}
          label={<span className="demibold">{g.name}</span>}
          value={g.uid}
          checked={this.allInspectorGroupSelected(g.uid)}
          indeterminate={this.inspectorGroupSelected(g.uid) && !this.allInspectorGroupSelected(g.uid)}
          onChange={() => this.selectInspectorGroup(g)}
          testId={`inspector-select-checkbox${g.name}`}
        />
        <div className="col-xl-12 col-lg-12 accuracy-inspector-flyout__checkbox-list ps-2">
          {g.inspections.map((i) => (
            <QESCheckbox
              id={uniqueId(i.name)}
              key={i.name}
              label={i.name}
              value={i.uid}
              checked={this.state.selectedInspectors.includes(i.uid)}
              onChange={(e) => this.selectInspector(e.target.value)}
              testId={`inspector-select-checkbox${i.name}`}
            />
          ))}
        </div>
      </div>
    ));

    return (
      <div>
        <QESCheckbox
          containerClassName="me-4 justify-content-end"
          className="mt-0"
          id={uniqueId("selectAll")}
          key="select-all"
          label={<span className="demibold">Select All</span>}
          checked={this.allInspectorsSelected()}
          indeterminate={this.state.selectedInspectors.length > 0 && !this.allInspectorsSelected()}
          onChange={() => this.selectAll()}
          testId={"inspector-select-all-checkbox"}
        />
        {inspectionGroupRender}
      </div>
    );
  }

  private selectInspector(inspector: string) {
    let selectedInspectors = [...this.state.selectedInspectors];
    if (selectedInspectors.includes(inspector))
      selectedInspectors = selectedInspectors.filter((i) => i !== inspector)
    else
      selectedInspectors.push(inspector);

    this.setState({
      selectedInspectors
    });
  }

  private selectInspectorGroup(inspectionGroup: AccuracyTemplateInspectionGroup) {
    const allInspectionGroupSelected = this.allInspectorGroupSelected(inspectionGroup.uid);
    let selectedInspectors = [... this.state.selectedInspectors];
    const inspectorUids = flatMap(inspectionGroup.inspections, (i) => i.uid);
    if (allInspectionGroupSelected) {
      selectedInspectors = selectedInspectors.filter((i) => !inspectorUids.includes(i));
    }
    else {
      const unselectedInspectors = inspectorUids.filter((i) => !selectedInspectors.includes(i));
      selectedInspectors = selectedInspectors.concat(unselectedInspectors)
    }

    this.setState({
      selectedInspectors
    })
  }

  private selectAll() {
    const selectedInspectors = this.state.selectedInspectors.length > 0
      ? []
      : this.getAllInspectorUids();

    this.setState({
      selectedInspectors
    })
  }

  private getAllInspectorUids(): string[] {
    return flatMap(this.inspectionGroups, (g) => g.inspections).map((i) => i.uid);
  }

  private allInspectorsSelected(): boolean {
    const allUids = this.getAllInspectorUids();
    return allUids.length > 0 && allUids.every((uid) => this.state.selectedInspectors.includes(uid));
  }

  private inspectorGroupSelected(groupUid: string) {
    const inspectorUids = flatMap(this.inspectionGroups.find((ig) => ig.uid === groupUid).inspections, (i) => i.uid);
    return this.state.selectedInspectors.some((i) => inspectorUids.includes(i));
  }

  private allInspectorGroupSelected(groupUid: string) {
    const inspectorUids = flatMap(this.inspectionGroups.find((ig) => ig.uid === groupUid).inspections, (i) => i.uid);
    return inspectorUids.every((i) => this.state.selectedInspectors.includes(i));
  }
}

export default withRouter(AccuracyInspectorSelectFlyout);